import { useState, useRef, useEffect } from "react";
import { Header } from "../components/layout/Header";
import {
  SparklesIcon,
  ChevronRightIcon,
  CopyIcon,
  CheckIcon,
  DownloadIcon,
} from "../components/ui/Icons";
import { skillCreatorApi, type SkillCreatorMessage, type SkillFile } from "../lib/api";

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <div
        className="h-2 w-2 animate-pulse rounded-full bg-amber/60"
        style={{ animationDelay: "0ms" }}
      />
      <div
        className="h-2 w-2 animate-pulse rounded-full bg-amber/60"
        style={{ animationDelay: "150ms" }}
      />
      <div
        className="h-2 w-2 animate-pulse rounded-full bg-amber/60"
        style={{ animationDelay: "300ms" }}
      />
    </div>
  );
}

function MessageBubble({ message }: { message: SkillCreatorMessage }) {
  const isUser = message.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 ${
          isUser ? "bg-amber text-base" : "bg-surface/80 text-secondary ring-1 ring-elevated"
        }`}
      >
        <div className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</div>
      </div>
    </div>
  );
}

function SkillFilePreview({
  files,
  copiedFile,
  onCopy,
  onDownload,
}: {
  files: SkillFile[];
  copiedFile: string | null;
  onCopy: (filename: string, content: string) => void;
  onDownload: (filename: string, content: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <SparklesIcon size={18} className="text-amber" />
        <h3 className="font-display text-lg font-bold text-primary">Generated Skill Files</h3>
      </div>

      {files.map((file) => (
        <div
          key={file.filename}
          className="overflow-hidden rounded-xl border border-elevated bg-surface/50"
        >
          <div className="flex items-center justify-between border-b border-elevated px-4 py-2">
            <code className="font-mono text-xs text-amber">{file.filename}</code>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onCopy(file.filename, file.content)}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted transition-colors hover:bg-elevated hover:text-secondary"
              >
                {copiedFile === file.filename ? (
                  <>
                    <CheckIcon size={12} /> Copied
                  </>
                ) : (
                  <>
                    <CopyIcon size={12} /> Copy
                  </>
                )}
              </button>
              <button
                onClick={() => onDownload(file.filename, file.content)}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted transition-colors hover:bg-elevated hover:text-secondary"
              >
                <DownloadIcon size={12} /> Download
              </button>
            </div>
          </div>
          <pre className="max-h-80 overflow-auto p-4 font-mono text-xs leading-relaxed text-secondary">
            {file.content}
          </pre>
        </div>
      ))}

      {files.length > 1 && (
        <button
          onClick={() => {
            // Download all files as individual downloads
            files.forEach((f) => onDownload(f.filename, f.content));
          }}
          className="btn-primary w-full text-sm"
        >
          <DownloadIcon size={16} />
          Download All Files
        </button>
      )}
    </div>
  );
}

export function SkillCreatorPage() {
  const [messages, setMessages] = useState<SkillCreatorMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skillFiles, setSkillFiles] = useState<SkillFile[] | null>(null);
  const [copiedFile, setCopiedFile] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage: SkillCreatorMessage = { role: "user", content: trimmed };
    const updatedMessages = [...messages, userMessage];

    setMessages(updatedMessages);
    setInput("");
    setError(null);
    setIsLoading(true);

    try {
      const result = await skillCreatorApi.chat(updatedMessages);

      const assistantMessage: SkillCreatorMessage = {
        role: "assistant",
        content: result.response,
      };

      setMessages([...updatedMessages, assistantMessage]);

      if (result.skillFiles) {
        setSkillFiles(result.skillFiles);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to get response";
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleCopy = (filename: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedFile(filename);
    setTimeout(() => setCopiedFile(null), 2000);
  };

  const handleDownload = (filename: string, content: string) => {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Use just the file name part for download
    a.download = filename.split("/").pop() || filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const startOver = () => {
    setMessages([]);
    setSkillFiles(null);
    setError(null);
    setInput("");
    inputRef.current?.focus();
  };

  return (
    <div className="flex min-h-screen flex-col bg-base">
      <Header />

      <div className="flex flex-1 flex-col pt-16">
        {/* Chat area */}
        <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 sm:px-6">
          {/* Welcome message if no messages yet */}
          {messages.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center pb-20">
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-amber/20">
                <SparklesIcon size={32} className="text-amber" />
              </div>
              <h1 className="mb-3 text-center font-display text-2xl font-bold text-primary md:text-3xl">
                Create a Skill
              </h1>
              <p className="mb-8 max-w-md text-center text-muted">
                Describe what you want your Claude Code skill to do, and I'll help you build it step
                by step.
              </p>

              {/* Suggestion chips */}
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  "A skill for setting up Tailwind CSS projects",
                  "A skill that knows my company's coding standards",
                  "A skill for generating API documentation",
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="rounded-lg border border-elevated bg-surface/50 px-3 py-2 text-left text-sm text-muted transition-colors hover:border-amber/30 hover:text-secondary"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.length > 0 && (
            <div className="flex-1 space-y-4 py-6">
              {messages.map((msg, i) => (
                <MessageBubble key={i} message={msg} />
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl bg-surface/80 ring-1 ring-elevated">
                    <TypingIndicator />
                  </div>
                </div>
              )}
              {error && (
                <div className="flex justify-center">
                  <div className="rounded-lg bg-danger/10 px-4 py-2 text-sm text-danger">
                    {error}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Skill files panel */}
          {skillFiles && (
            <div className="mb-6 rounded-xl border border-amber/20 bg-surface/30 p-6">
              <SkillFilePreview
                files={skillFiles}
                copiedFile={copiedFile}
                onCopy={handleCopy}
                onDownload={handleDownload}
              />
              <div className="mt-4 flex justify-center">
                <button onClick={startOver} className="btn-secondary text-sm">
                  Create Another Skill
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Input area - fixed at bottom */}
        <div className="sticky bottom-0 border-t border-surface bg-base/95 backdrop-blur-xl">
          <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6">
            <div className="flex items-end gap-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe what you want your skill to do..."
                rows={1}
                className="input-field max-h-32 min-h-[44px] flex-1 resize-none py-3"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber text-base transition-colors hover:bg-amber-hover disabled:opacity-50 disabled:hover:bg-amber"
              >
                <ChevronRightIcon size={20} />
              </button>
            </div>
            <p className="mt-2 text-center font-mono text-xs text-muted">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
